import { useCallback, useEffect } from 'react';
import { ReactSVG } from 'react-svg';
import { debounceTime } from 'rxjs/operators';
import useCaro from '~/hooks/useCaro';

import Store from '~/store/undux';
import { secondsToTimecode } from '~/utils/utils';
import useSetVideo from '~/hooks/useSetVideo';

/**
 * YouTube recommendations carousel; stays in sync with playlistInfo and scrolls to active video.
 * @param {object} props.playlist - List of video items with id, image_url, title, duration.
 */

const getDuration = (duration) => {
  const timecode = secondsToTimecode(duration).split(':');
  const HH = parseInt(timecode[0], 10) > 0 ? `${parseInt(timecode[0], 10)}:` : '';
  const MM = parseInt(timecode[1], 10) > 0 ? `${parseInt(timecode[1], 10)}` : '0';
  return `${HH}${MM}:${timecode[2]}`;
};

const setDefaultThumbnail = (e) => {
  e.target.src = '/img/recommendation_fpo.jpg';
};

const YouTubeCarouselRecommendations = (props) => {
  const { playlist, store } = props;
  const { embla, viewportRef, scrollNext, scrollPrev, setSelectedIndex } = useCaro({
    loop: true,
    align: 'start',
    slidesToScroll: 1
  });

  const playlistInfo = store.get('playlistInfo');
  const fallbackIndex = store.get('playlistIndex');
  const activeIndex = typeof playlistInfo?.playlistIndex === 'number'
    ? playlistInfo.playlistIndex
    : fallbackIndex;
  const nextIndex = playlist?.length > 1 ? (activeIndex + 1) % playlist.length : null;

  // Scroll to active index on mount
  useEffect(() => {
    if (activeIndex && embla) {
      embla.scrollTo(activeIndex);
    }
  }, [activeIndex, embla]);
  
  const { updateStore } = useSetVideo();
  const handleClick = useCallback(
    (e) => {
      e.preventDefault();
      const index = parseInt(e.currentTarget.dataset.index, 10);
      if (Number.isNaN(index)) return;
      const playlistInfo = store.get('playlistInfo');
      embla.scrollTo(index);
      updateStore(
        playlistInfo?.trayIndex,
        index,
        playlistInfo?.playlists,
        playlistInfo?.activeChip,
        undefined,
        undefined
      );
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [embla, store, updateStore]
  );

  store
    .on('playlistIndex')
    .pipe(debounceTime(1))
    .subscribe(index => {
      setSelectedIndex(index);
      embla?.scrollTo?.(index);
    });

  return (
    <div className='caro-rec'>
      <div className='constrain'>
        <div className='caro-rec__viewport' ref={viewportRef}>
          <div className='caro-rec__container'>
            {playlist.map((slide, i) => (
              <button
                type='button'
                className='caro-rec__recomendation'
                key={`recomendation-item-${slide.id}`}
                onClick={handleClick}
                data-index={i}
              >
                <div className='caro-rec__recomendation-img'>
                  {slide.image_url && (
                    <img
                      src={slide.image_url}
                      onError={setDefaultThumbnail}
                      alt={slide.title}
                      loading='lazy'
                    />
                  )}
                  {/* Show badges for active and next video, else show duration. */}
                  {(i === activeIndex || i === nextIndex) ? (
                    <>
                      {i === activeIndex && (
                        <div className='caro-rec__badge caro-rec__nowplaying'>Playing</div>
                      )}
                      {i === nextIndex && (
                        <div className='caro-rec__badge caro-rec__nextup'>Up Next</div>
                      )}
                    </>
                  ) : (
                    <>
                      {slide.duration && (
                        <div className='block-videos__duration'>{getDuration(slide.duration)}</div>
                      )}
                    </>
                  )}
                </div>
                <h4>{slide.title}</h4>
              </button>
            ))}
          </div>
        </div>
        {playlist.length > 5 && (
          <>
            <button
              className='caro-rec__arrow caro-rec__arrow--prev'
              type='button'
              onClick={scrollPrev}
              aria-label='Previous Recommendation'
            >
              <ReactSVG src='/img/icons/arrow-hollow_left.svg' />
            </button>
            <button
              className='caro-rec__arrow caro-rec__arrow--next'
              type='button'
              onClick={scrollNext}
              aria-label='Next Recommendation'
            >
              <ReactSVG src='/img/icons/arrow-hollow_left.svg' />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default Store.withStore(YouTubeCarouselRecommendations);
